// Phase 24c · User subscription management
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Crown, XCircle, Sparkles, RefreshCcw, AlertTriangle, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function eur(cents) { return `$${((cents || 0) / 100).toFixed(2)}`; }
function fmt(dt) { if (!dt) return ""; try { return new Date(dt).toLocaleDateString(); } catch { return dt; } }

const DUNNING_STYLE = {
    healthy: "bg-[#CCCCCC]/15 text-[#CCCCCC]",
    past_due: "bg-neutral-500/15 text-neutral-300",
    grace: "bg-neutral-500/15 text-neutral-300",
    canceled_for_nonpayment: "bg-[#FAFAFA]/15 text-[#FAFAFA]",
};

export default function AccountSubscriptions() {
    const [subs, setSubs] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [msg, setMsg] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/api/subscriptions/me/current`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); setLoading(false); return; }
            if (r.ok) setSubs(((await r.json()) || {}).items || []);
            const c = await fetch(`${API}/api/subscriptions/catalog`, { credentials: "include" });
            if (c.ok) setCatalog(((await c.json()) || {}).tiers || []);
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => {
        document.title = "My subscriptions · Global Lead Suite";
        load();
    }, [load]);

    const cancel = async (subId) => {
        if (!window.confirm("Cancel this subscription? You keep access until the end of the current period.")) return;
        const r = await fetch(`${API}/api/subscriptions/me/cancel`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ subscription_id: subId, reason: "user_initiated" }),
        });
        if (r.ok) { setMsg("Cancellation scheduled at period end."); load(); }
    };
    const reactivate = async (subId) => {
        const r = await fetch(`${API}/api/subscriptions/me/reactivate`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ subscription_id: subId }),
        });
        if (r.ok) { setMsg("Subscription reactivated."); load(); }
    };
    const changeTier = async (subId, newTier, newInterval) => {
        const r = await fetch(`${API}/api/subscriptions/me/change-tier`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ subscription_id: subId, new_tier_slug: newTier, new_interval: newInterval }),
        });
        if (r.ok) {
            const d = await r.json();
            setMsg(`Changed to ${newTier} (${newInterval}). Proration: ${eur(d.proration_cents || 0)}.`);
            load();
        }
    };

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center" data-testid="subscriptions-disabled">
                    <Crown className="mx-auto text-[#CCCCCC] mb-3" />
                    <h1 className="text-xl font-semibold">Subscriptions coming soon</h1>
                    <p className="text-neutral-400 text-sm mt-2">SubscriptionEngine is being finalized.</p>
                    <Link to="/app/subscription" className="mt-3 inline-block text-sm text-[#CCCCCC] hover:underline">View plans →</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-4xl mx-auto px-4 py-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold" data-testid="account-subscriptions-title">My subscriptions</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">Manage your premium tiers.</p>
                    </div>
                    <Link to="/account/billing" className="text-xs text-[#CCCCCC] hover:underline">Billing history →</Link>
                </div>

                {msg && (
                    <div className="mb-4 rounded-xl border border-[#CCCCCC]/30 bg-[#CCCCCC]/10 px-3 py-2 text-xs text-[#CCCCCC] inline-flex items-center gap-2" data-testid="account-subs-msg">
                        <CheckCircle size={12} /> {msg}
                    </div>
                )}
                {loading && <div className="text-sm text-neutral-500 mb-3">Loading…</div>}

                {subs.length === 0 && !loading && (
                    <div className="rounded-xl border border-dashed border-[#1A1A1A] p-8 text-center" data-testid="account-subs-empty">
                        <Sparkles className="mx-auto text-neutral-500 mb-3" />
                        <p className="text-sm text-neutral-400 mb-4">No active subscriptions yet.</p>
                        <Link to="/app/subscription" className="inline-flex items-center gap-1 text-xs px-4 py-2 rounded-full bg-white text-black">
                            Explore Premium plans
                        </Link>
                    </div>
                )}

                <div className="space-y-3" data-testid="account-subs-list">
                    {subs.map((s) => (
                        <div
                            key={s.stripe_id}
                            data-testid={`sub-row-${s.stripe_id}`}
                            className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4"
                        >
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <div>
                                    <p className="text-sm font-semibold flex items-center gap-2">
                                        <Crown size={14} className="text-neutral-400" />
                                        {(s.tier_slug || "").replace(/^premium_/, "").replace(/^./, c => c.toUpperCase())}
                                    </p>
                                    <p className="text-[11px] text-neutral-500">
                                        {s.billing_interval || "month"} · next: {fmt(s.current_period_end)}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-1 items-end">
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${DUNNING_STYLE[s.dunning_state || "healthy"]}`}>
                                        {s.dunning_state || "healthy"}
                                    </span>
                                    {s.cancels_at_period_end && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-500/15 text-neutral-400">
                                            cancels @ period end
                                        </span>
                                    )}
                                </div>
                            </div>
                            {["past_due", "grace"].includes(s.dunning_state) && (
                                <div className="rounded-lg border border-neutral-500/30 bg-neutral-500/10 px-3 py-2 text-xs text-neutral-300 flex items-center gap-2 mb-2" data-testid={`sub-dunning-warn-${s.stripe_id}`}>
                                    <AlertTriangle size={12} /> Payment issue detected. Retry scheduled.
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                                {!s.cancels_at_period_end && s.status === "active" && (
                                    <button
                                        onClick={() => cancel(s.stripe_id)}
                                        data-testid={`sub-cancel-${s.stripe_id}`}
                                        className="text-[11px] px-3 py-1.5 rounded-full border border-[#FAFAFA]/40 text-[#FAFAFA] hover:bg-[#FAFAFA]/10 inline-flex items-center gap-1"
                                    >
                                        <XCircle size={12} /> Cancel
                                    </button>
                                )}
                                {(s.cancels_at_period_end || s.dunning_state === "grace") && (
                                    <button
                                        onClick={() => reactivate(s.stripe_id)}
                                        data-testid={`sub-reactivate-${s.stripe_id}`}
                                        className="text-[11px] px-3 py-1.5 rounded-full border border-[#CCCCCC]/40 text-[#CCCCCC] hover:bg-[#CCCCCC]/10 inline-flex items-center gap-1"
                                    >
                                        <RefreshCcw size={12} /> Reactivate
                                    </button>
                                )}
                                <select
                                    onChange={(e) => {
                                        const [t, i] = e.target.value.split("|");
                                        if (t) changeTier(s.stripe_id, t, i);
                                    }}
                                    defaultValue=""
                                    data-testid={`sub-change-tier-${s.stripe_id}`}
                                    className="text-[11px] bg-[#0A0A0A] border border-[#1A1A1A] rounded-full px-3 py-1"
                                >
                                    <option value="">Change tier…</option>
                                    {catalog.filter(t => t.tier_slug !== s.tier_slug).map(t => (
                                        <React.Fragment key={t.tier_slug}>
                                            <option value={`${t.tier_slug}|month`}>→ {t.name} (monthly)</option>
                                            <option value={`${t.tier_slug}|year`}>→ {t.name} (annual -20%)</option>
                                        </React.Fragment>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
